import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ClipboardCheck,
    CheckCircle2,
    ChevronRight,
    ChevronDown,
    Inbox
} from "lucide-react";
import dayjs from "dayjs";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { EmployeeDetails } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { TEAM_TIMESHEET_SUMMARY_URL, TEAM_TIMESHEET_URL } from "../../constants/api";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const isDarkMode = () =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");

// Map the backend employee_profile_details() shape onto EmployeeDetails props.
const toEmployeeDetails = (employee) => ({
    ...employee,
    first_name: employee?.employee_name || "-",
    last_name: "",
    id: employee?.employee_id || "",
    designation_id: employee?.designation
        ? { id: employee.designation.id, designation_name: employee.designation.name }
        : { id: 0, designation_name: "" },
});

const DayEndApprovalCard = ({ days = 7 }) => {
    const navigate = useNavigate();
    const { fetch, update } = useApiMethods();

    const canReview = useMemo(() => {
        const isAdmin =
            JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
            JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
        if (isAdmin) return true;
        const perm = checkUserPermissionFunc("employee", "Team Timesheet", "SubMenu") || {};
        return !!(perm.permission || perm.edit);
    }, []);

    const [chart, setChart] = useState([]);
    const [pending, setPending] = useState([]);
    const [totals, setTotals] = useState({ pending: 0, approved: 0 });
    const [loading, setLoading] = useState(false);
    const [approvingId, setApprovingId] = useState(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(pending.length > 0);
    }, [pending.length]);

    const loadSummary = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(TEAM_TIMESHEET_SUMMARY_URL, { days });
            if (res?.status === "success" && res.data) {
                setChart(res.data.chart || []);
                setPending(res.data.pending || []);
                setTotals(res.data.totals || { pending: 0, approved: 0 });
            }
        } catch (e) {
            // Silent on the dashboard — the card just shows its empty state.
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    useEffect(() => {
        if (canReview) loadSummary();
    }, [canReview, loadSummary]);

    const quickApprove = async (row) => {
        setApprovingId(row.id);
        try {
            const res = await update(TEAM_TIMESHEET_URL, { id: row.id, action: "approved" });
            if (res?.status === "success") {
                ToastMsg("success", "Day-end report approved");
                setPending((prev) => prev.filter((r) => r.id !== row.id));
                setTotals((prev) => ({
                    pending: Math.max(0, prev.pending - 1),
                    approved: prev.approved + 1,
                }));
            } else {
                ToastMsg("error", res?.message || "Failed to approve");
            }
        } catch (e) {
            ToastMsg("error", "Something went wrong while approving");
        } finally {
            setApprovingId(null);
        }
    };

    const dark = isDarkMode();
    const chartData = useMemo(
        () => ({
            labels: chart.map((c) => dayjs(c.date).format("DD MMM")),
            datasets: [
                {
                    label: "Pending",
                    data: chart.map((c) => c.pending),
                    backgroundColor: "#f59e0bcc",
                    hoverBackgroundColor: "#f59e0b",
                    borderRadius: 6,
                    borderSkipped: false,
                    maxBarThickness: 22,
                },
                {
                    label: "Approved",
                    data: chart.map((c) => c.approved),
                    backgroundColor: "#10b981cc",
                    hoverBackgroundColor: "#10b981",
                    borderRadius: 6,
                    borderSkipped: false,
                    maxBarThickness: 22,
                },
            ],
        }),
        [chart]
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: "bottom",
                    labels: { color: dark ? "#9ca3af" : "#4b5563", boxWidth: 12, font: { size: 11 } },
                },
                tooltip: {
                    backgroundColor: dark ? "#1f2937" : "#111827",
                    titleColor: "#fff",
                    bodyColor: "#fff",
                    padding: 10,
                    cornerRadius: 8,
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { color: dark ? "#9ca3af" : "#4b5563", font: { size: 11 } },
                },
                y: {
                    beginAtZero: true,
                    ticks: { color: dark ? "#9ca3af" : "#4b5563", precision: 0 },
                    grid: { color: dark ? "rgba(75,85,99,0.2)" : "rgba(229,231,235,0.8)" },
                },
            },
        }),
        [dark]
    );

    if (!canReview) return null;

    const totalRequests = totals.pending + totals.approved;

    const pendingPercentage =
        totalRequests > 0
            ? Math.round((totals.pending / totalRequests) * 100)
            : 0;

    const approvedPercentage =
        totalRequests > 0
            ? Math.round((totals.approved / totalRequests) * 100)
            : 0;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 dark:border-gray-700 overflow-hidden">

            {/* Header */}
            <div
                className="p-5 cursor-pointer"
                onClick={() => setExpanded(prev => !prev)}
            >
                <div className="flex items-center justify-between">

                    <div className="flex items-center gap-3">
                        <ClipboardCheck className="w-5 h-5 text-sidebar-primary" />

                        <div>
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                                Day-End Approvals
                            </h2>

                            <p className="text-xs text-muted-foreground mt-1">
                                Team Timesheet Approval Summary
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate("/employee/team/timesheet");
                            }}
                            className="text-xs font-medium text-sidebar-primary hover:underline flex items-center gap-1"
                        >
                            Open team timesheet
                            <ChevronRight className="w-3 h-3" />
                        </button>

                        <ChevronDown
                            className={`w-4 h-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""
                                }`}
                        />
                    </div>
                </div>

                {/* Compact Summary */}
                {
                    !expanded && (
                        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">

                            <div>
                                <div className="flex justify-between mb-1">
                                    <span className="text-xs font-medium text-amber-600">
                                        Pending
                                    </span>

                                    <span className="text-xs font-semibold text-amber-600">
                                        {totals.pending}
                                    </span>
                                </div>

                                <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                    <div
                                        className="h-full bg-amber-500 transition-all duration-500"
                                        style={{ width: `${pendingPercentage}%` }}
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between mb-1">
                                    <span className="text-xs font-medium text-green-600">
                                        Approved
                                    </span>

                                    <span className="text-xs font-semibold text-green-600">
                                        {totals.approved}
                                    </span>
                                </div>

                                <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                    <div
                                        className="h-full bg-green-500 transition-all duration-500"
                                        style={{ width: `${approvedPercentage}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    )
                }
            </div>

            {/* Expanded Section */}
            {expanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-5">

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                        {/* Existing Chart */}
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                    Pending {totals.pending}
                                </span>

                                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                                    Approved {totals.approved}
                                </span>

                                <span className="text-xs text-gray-400 ml-auto">
                                    Last {days} days
                                </span>
                            </div>

                            <div className="h-56">
                                {loading ? (
                                    <div className="h-full w-full animate-pulse bg-gray-100 dark:bg-gray-700 rounded-lg" />
                                ) : (
                                    <Bar
                                        data={chartData}
                                        options={chartOptions}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Existing Pending List */}
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                                Awaiting Approval ({pending.length})
                            </div>

                            <div className="space-y-2 overflow-y-auto max-h-[350px]">

                                {loading ? (
                                    <div className="h-20 w-full animate-pulse bg-gray-100 dark:bg-gray-700 rounded-lg" />
                                ) : pending.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
                                        <Inbox className="w-8 h-8 mb-2 opacity-50" />
                                        <p className="text-sm">
                                            No reports awaiting approval
                                        </p>
                                    </div>
                                ) : (
                                    pending.map((row) => (
                                        <div
                                            key={row.id}
                                            className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                                        >
                                            <div className="min-w-0">
                                                <EmployeeDetails
                                                    details={toEmployeeDetails(row.employee)}
                                                    size="xs"
                                                    isFromInbox
                                                />

                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                                                    {dayjs(row.date).format("DD MMM YYYY")}
                                                    {" • "}
                                                    {row.actual_hours ?? 0}h
                                                    {" • "}
                                                    {row.tasks_count || 0} tasks
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">

                                                <Button
                                                    size="sm"
                                                    disabled={approvingId === row.id}
                                                    onClick={() => quickApprove(row)}
                                                    className="h-7 bg-green-600 hover:bg-green-700 text-white"
                                                >
                                                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                                    Approve
                                                </Button>

                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        navigate("/employee/team/timesheet")
                                                    }
                                                >
                                                    Review
                                                </Button>

                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default DayEndApprovalCard;
